/* eslint class-methods-use-this:0 */

import hyperid from 'hyperid';
import ProxiedClass from './ProxiedClass';

export default class WorkerProxy {
  /** @type {Function} */
  getId = null;

  /** @type {Object} */
  registeredClasses = {};

  /** @type {Object} subscriptions Key-based set of subscription callbacks keyed by subscription ID. */
  subscriptions = {};

  /** @type {Worker} */
  worker = null;

  /**
   * Instantiates a proxied worker using the given worker.
   *
   * @param {Worker} worker
   */
  constructor(worker) {
    this.worker = worker;
    this.listenForMessages();
    this.getId = hyperid({ fixedLength: true });
  }

  /**
   * Proxies a method call to the worker using the given arguments.
   *
   * @param  {...any} args
   * @returns {any}
   */
  async callMethod(...args) {
    let action;
    let payload;

    if (args[0].startsWith('CLASS_METHOD_CALL_')) {
      // Registered classes' method calls
      const [classNameAction, methodName, methodPayload] = args;
      const className = classNameAction.substring(18);
      action = 'callClassMethod';
      payload = {
        className,
        methodName,
        methodPayload,
      };
    } else {
      // WorkerManager method calls
      [action, payload] = args;
    }

    const uid = this.getId();
    const subscriptionId = `${action}_${uid}`;

    this.postMessage({ action, payload, uid });
    const responsePayload = await this.promisifyMessaging(subscriptionId, { action, payload, uid });

    return responsePayload;
  }

  /**
   * Retrieves a registered worker class from the worker (and caches).
   *
   * @param {String} className
   * @returns {Object}
   */
  async get(className) {
    if (!this.registeredClasses[className]) {
      const classMethods = await this.callMethod('getClassDefinition', className);
      this.registeredClasses[className] = new ProxiedClass(this, className, classMethods);
    }
    return this.registeredClasses[className];
  }

  /**
   * Adds an event listener on the worker that listens for messages.
   */
  listenForMessages() {
    this.worker.addEventListener('message', ({ data }) => {
      const { action, error = null, errorStack = '', payload } = data;
      const subscriptions = this.subscriptions[action];
      if (subscriptions) {
        subscriptions.forEach((callback) => {
          callback({ error, errorStack, payload });
        });
      }
    });
  }

  /**
   * Posts a message to the worker thread.
   *
   * @param {Object} message
   * @param {String} message.action
   * @param {any} message.payload
   * @param {String} message.uid
   */
  postMessage({ action, payload, uid }) {
    this.worker.postMessage({ action, payload, uid });
  }

  /**
   * Waits for a "ready" response from the worker.
   *
   * @returns {Promise}
   */
  ready() {
    return this.promisifyMessaging('READY', { action: 'READY' });
  }

  /**
   * Adds a callback identified the given subscription ID.
   *
   * @param {String} id
   * @param {Function} callback
   */
  subscribe(id, callback) {
    this.subscriptions[id] = [...(this.subscriptions[id] || []), callback];
  }

  /**
   * Removes the callback that uses the given subscription ID.
   *
   * @param {String} id
   */
  unsubscribe(id, callback) {
    if (!callback) {
      delete this.subscriptions[id];
    } else if (this.subscriptions[id]) {
      this.subscriptions[id] = this.subscriptions[id].filter((subscription) => subscription !== callback);
    }
  }

  /**
   * Creates a one-time subscription that resolves or rejects based on the
   * subscription response.
   *
   * @param {String} subscriptionId
   * @param {Object} messageInfo
   * @returns {Promise}
   */
  promisifyMessaging(subscriptionId, messageInfo = {}) {
    return new Promise((resolve, reject) => {
      this.subscribe(subscriptionId, ({ error, errorStack, payload }) => {
        this.unsubscribe(subscriptionId);
        if (error) {
          let origin = subscriptionId;
          if (messageInfo && messageInfo.action === 'callClassMethod') {
            const { payload: payloadInfo } = messageInfo;
            const { className, methodName } = payloadInfo;
            origin = `${className}->${methodName}()`;
          }
          const callError = new Error(`${origin}: ${error}`);
          callError.stack = errorStack;
          reject(callError);
        } else {
          resolve(payload);
        }
      });
    });
  }
}
